// src/services/studyReportApproval.js
//
// Admin-in-the-loop study numbers. When a coordinator's weekly report says
// patients enrolled or screen-failed on a study, the study page shows it to an
// admin with what the study's Enrolled and Screen Failures would become. Only
// when the admin approves are those two numbers increased; Dismiss leaves the
// study alone. Either way the report is stamped
// (`studyApprovals.<studyDocId>`), so it can never be applied twice.
//
// Only new-format reports (format 2, from the patient-level form) are ever
// pending; reports from before it never are. Only ENROLLED and
// SCREEN_FAILURES change; no other study field.
//
// Pure and Firebase-free so it is unit-tested (studyReportApproval.test.js).

/** A study number as stored (the legacy /edit page saves text): "12" -> 12, "" -> 0. */
export const studyNumber = (v) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

const toMs = (ts) => (ts?.toMillis ? ts.toMillis() : typeof ts === "number" ? ts : 0);

/**
 * The reports waiting for an admin decision on this study, oldest period first.
 * reports: weekly_reports docs as { id, ...data }.
 */
export const pendingStudyEntries = (reports = [], studyDocId) =>
  reports
    .filter((r) => r && r.format === 2 && !r.studyApprovals?.[studyDocId])
    .map((r) => {
      const entry = (r.studies || []).find((s) => s.studyDocId === studyDocId);
      if (!entry) return null;
      const enrollments = studyNumber(entry.enrollments);
      const screenFailures = studyNumber(entry.screenFailures);
      if (!enrollments && !screenFailures) return null;
      return {
        reportId: r.id,
        coordinator: r.coordinator || "",
        periodStart: r.periodStart || "",
        periodEnd: r.periodEnd || "",
        weekOf: r.weekOf || "",
        submittedAtMs: toMs(r.submittedAt),
        enrollments,
        screenFailures,
      };
    })
    .filter(Boolean)
    .sort(
      (a, b) => String(a.periodStart).localeCompare(String(b.periodStart)) || a.submittedAtMs - b.submittedAtMs
    );

/**
 * Study ids with at least one report waiting for an admin's OK (the same rule as
 * the study page's card), for highlighting cards on the Dashboard.
 */
export const studiesWithPendingNumbers = (reports = []) => {
  const ids = new Set();
  reports.forEach((r) =>
    (r?.studies || []).forEach((s) => {
      if (s?.studyDocId && !ids.has(s.studyDocId) && pendingStudyEntries([r], s.studyDocId).length) {
        ids.add(s.studyDocId);
      }
    })
  );
  return ids;
};

/**
 * What the study becomes if these entries are approved, applied in order.
 * Returns { before, after, steps: [{ reportId, before, after }] }.
 */
export const planStudyUpdate = (study, entries = []) => {
  const before = { ENROLLED: studyNumber(study?.ENROLLED), SCREEN_FAILURES: studyNumber(study?.SCREEN_FAILURES) };
  let running = { ...before };
  const steps = entries.map((e) => {
    const stepBefore = running;
    running = {
      ENROLLED: running.ENROLLED + studyNumber(e.enrollments),
      SCREEN_FAILURES: running.SCREEN_FAILURES + studyNumber(e.screenFailures),
    };
    return { reportId: e.reportId, before: stepBefore, after: running };
  });
  return { before, after: running, steps };
};

/** The stamp written on a report for this study. `at` is added by the service. */
export const buildStudyStamp = ({ status, by, entry, step }) => ({
  status, // "approved" | "dismissed"
  by: String(by || "").toLowerCase(),
  enrolledAdded: status === "approved" ? studyNumber(entry?.enrollments) : 0,
  screenFailuresAdded: status === "approved" ? studyNumber(entry?.screenFailures) : 0,
  ...(step ? { before: step.before, after: step.after } : {}),
});

/** How a coordinator sees one study's decision on their report. */
export const approvalLabel = (report, studyDocId) => {
  const stamp = report?.studyApprovals?.[studyDocId];
  if (stamp?.status === "approved") return "Study numbers updated by an admin";
  if (stamp?.status === "dismissed") return "Not added to the study numbers";
  const entry = (report?.studies || []).find((s) => s.studyDocId === studyDocId);
  if (!entry || (!studyNumber(entry.enrollments) && !studyNumber(entry.screenFailures))) return "";
  return "Waiting for an admin to approve the study numbers";
};
